/**
 * `/flows/:flow_name/edit` page — phase 33a.
 *
 * Form editor for marketing-flow YAML definitions. Reads the current
 * flow via `GET /api/flows/:flow_name`, lets operators edit name /
 * trigger / exit_events / exit_after_hours / per-step delays + actions
 * + opt-out checks + per-channel input templates, then dispatches
 * `edit_marketing_flow` through the chokepoint (via `POST
 * /api/flows/:flow_name`).
 *
 * Per the brief's sub-decision H — page structure is:
 *   - Top: flow header (name read-only since name = filename; trigger
 *     event dropdown of MARKETING_FLOW_EVENT_NAMES; exit_events multi-
 *     select; exit_after_hours number input).
 *   - Body: ordered step list. Each step has: delay number input +
 *     opt_out_check text + action dropdown (filtered to `send_*` from
 *     /api/catalog) + per-channel input template fields (rendered
 *     based on selected action).
 *   - Footer: Save + Cancel + Reset-to-disk buttons.
 *
 * NO automated test coverage at phase 33a — per the post-phase-27
 * frontend-test-infra carve-out, React renderer files are visually
 * verified through Mario's Tier-2 playbook documented in the smoke
 * runbook.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { supabase } from "../lib/supabase";
import { EmailPreviewModal } from "../components/EmailPreviewModal";
import type { CatalogResponse } from "@layer/dashboard/types";

/**
 * The email template kind an email step renders through:
 *   - `template: nurture` → the bespoke per-step marketing design
 *     `flow:<flow>:step<N>` (editable via the override store);
 *   - a transactional `template:` (booking_confirmation, …) → that kind;
 *   - no `template:` → null (the step sends plaintext only, no HTML design).
 */
function emailKindForStep(
  flowName: string,
  step: FlowStepShape,
  index: number,
): string | null {
  if (step.action !== "send_email") return null;
  const t = step.template;
  if (t === undefined || t.length === 0) return null;
  if (t === "nurture") return `flow:${flowName}:step${String(index + 1)}`;
  return t;
}

/**
 * The Meta-approved WhatsApp template each TRANSACTIONAL flow sends via its
 * `member_transactional_wa_*` sibling saga (view-only — Meta owns the wording).
 * Keep in sync with `MEMBER_TRANSACTIONAL_WA_TEMPLATE_NAMES` in
 * `packages/saga-runtime/src/sagas/member-transactional-wa.ts` (the source of
 * truth). Home-session variants are shown where the approved copy names a venue.
 */
const WA_COMPANION_TEMPLATE: Record<
  string,
  { readonly name: string; readonly homeName?: string; readonly vars: string }
> = {
  booking_confirmation: {
    name: "booking_confirmation",
    homeName: "home_booking_confirmation",
    vars: "{{1}} session date/time · {{2}} trainer",
  },
  booking_cancellation: {
    name: "member_booking_cancellation",
    vars: "{{1}} first name · {{2}} session date/time · {{3}} credit line",
  },
  booking_rescheduled: {
    name: "member_booking_reschedule",
    vars: "{{1}} first name · {{2}} new time · {{3}} trainer",
  },
  booking_trainer_changed: {
    name: "member_trainer_change",
    homeName: "member_trainer_change_home",
    vars: "{{1}} first name · {{2}} session date/time · {{3}} new trainer",
  },
  payment_receipt: {
    name: "member_payment_receipt",
    vars: "{{1}} first name · {{2}} amount · {{3}} package",
  },
  refund_processed: {
    name: "member_refund",
    vars: "{{1}} first name · {{2}} amount · {{3}} package",
  },
};

interface FlowStepAnchorShape {
  trigger_field: string;
  offset_hours: number;
}

interface FlowStepShape {
  delay_after_previous_hours: number;
  action: string;
  opt_out_check?: string;
  exit_events?: ReadonlyArray<string>;
  anchor?: FlowStepAnchorShape;
  input_template: Record<string, unknown>;
  /**
   * The @layer/email-templates HTML-layout reference. Read-only here (set by
   * the flow author, edited under the Email Templates page) — but it MUST be
   * carried through Save, or the rendered email downgrades to plaintext.
   */
  template?: string;
}

interface FlowRepeatShape {
  last_steps: number;
  times: number | "infinite";
}

interface FlowShape {
  name: string;
  trigger_event: string;
  exit_events: ReadonlyArray<string>;
  exit_after_hours: number;
  default_locale?: string;
  steps: FlowStepShape[];
  /**
   * §BB's repeating tail. Declared here because the editor's GET returns the
   * flow by SPREAD, so this field ALREADY arrived in the browser and was
   * already POSTed back — it simply had no type and no control. Leaving it
   * undeclared is what made it invisible to edit and easy to break.
   */
  repeat?: FlowRepeatShape;
}

interface FlowFetchResponse {
  flow: FlowShape;
  studio_slug: string;
}

const MARKETING_FLOW_EVENT_NAMES = [
  "member.signed_up",
  "member.first_purchased",
  "member.purchased",
  "member.booking_made",
  "member.booking_cancelled",
  "member.session_completed",
] as const;

/**
 * Phase 34 sub-decision F — per-event date-ISO field allow-list. Used
 * by the per-step anchor widget to populate the `trigger_field`
 * dropdown. MUST stay in sync with `MARKETING_FLOW_EVENT_FIELDS` in
 * `packages/canonical-data/src/l2-yaml.ts` (server-side validator).
 */
const MARKETING_FLOW_EVENT_FIELDS: Record<string, ReadonlyArray<string>> = {
  "member.signed_up": ["signed_up_at"],
  "member.first_purchased": ["first_purchased_at"],
  "member.purchased": ["paid_at"],
  "member.booking_made": ["starts_at", "ends_at"],
  "member.booking_cancelled": ["cancelled_at"],
  "member.session_completed": ["ended_at"],
  "member.profile_updated": ["updated_at"],
  "member.membership_started": ["started_at"],
  "member.membership_cancelled": ["cancelled_at"],
  "member.credit_granted": ["granted_at"],
  "member.refunded": ["refunded_at"],
  "member.stripe_customer_registered": ["registered_at", "signed_up_at"],
  "member.payment_failed": ["failed_at"],
  "member.membership_payment_issue": ["occurred_at"],
  "member.session_rated": ["rated_at"],
  "member.booking_rescheduled": [
    "new_starts_at",
    "new_ends_at",
    "old_starts_at",
    "old_ends_at",
  ],
  "member.tagged": ["tagged_at"],
  "member.untagged": ["untagged_at"],
  "member.booking_trainer_changed": ["starts_at"],
  "member.checkout_abandoned": ["abandoned_at"],
};

function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const t = BigInt(Date.now());
  bytes[0] = Number((t >> 40n) & 0xffn);
  bytes[1] = Number((t >> 32n) & 0xffn);
  bytes[2] = Number((t >> 24n) & 0xffn);
  bytes[3] = Number((t >> 16n) & 0xffn);
  bytes[4] = Number((t >> 8n) & 0xffn);
  bytes[5] = Number(t & 0xffn);
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

export function FlowEdit(): JSX.Element {
  const { flow_name } = useParams<{ flow_name: string }>();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<FlowShape | null>(null);
  const [studioSlug, setStudioSlug] = useState<string | null>(null);
  const [original, setOriginal] = useState<FlowShape | null>(null);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  useEffect(() => {
    if (flow_name === undefined) {
      setError("Missing flow_name in URL");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get<FlowFetchResponse>(`/api/flows/${flow_name}`),
      api.catalog(),
    ])
      .then(([flowResp, catalogResp]) => {
        if (cancelled) return;
        setFlow(structuredClone(flowResp.flow));
        setOriginal(structuredClone(flowResp.flow));
        setStudioSlug(flowResp.studio_slug);
        setCatalog(catalogResp);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          setError(`api_error:${e.status}${e.code ? `:${e.code}` : ""}`);
        } else {
          setError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [flow_name]);

  // Build the action dropdown options: every action whose name starts
  // with `send_` AND admits `system:saga` in `allowed_actors` (sub-
  // decision B). Future channels (SMS / push / etc) auto-appear.
  const sendActionOptions = useMemo<string[]>(() => {
    if (catalog === null) return ["send_email", "send_whatsapp_message"];
    return catalog.actions
      .filter(
        (a) =>
          a.name.startsWith("send_") &&
          a.allowed_actors.includes("system:saga"),
      )
      .map((a) => a.name)
      .sort();
  }, [catalog]);

  const handleSave = useCallback(async () => {
    if (flow === null || flow_name === undefined) return;
    setSaving(true);
    setStatusMsg(null);
    setError(null);
    try {
      const requestId = uuidv7();
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token ?? "";
      const res = await fetch(`/api/flows/${flow_name}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          request_id: requestId,
          flow,
        }),
      });
      const text = await res.text();
      let body: { ok?: boolean; code?: string; message?: string };
      try {
        body = text.length > 0 ? JSON.parse(text) : {};
      } catch {
        body = {};
      }
      if (!res.ok || body.ok !== true) {
        setError(
          `save_failed (HTTP ${res.status}${body.code ? `, code=${body.code}` : ""}): ${body.message ?? text}`,
        );
      } else {
        setOriginal(structuredClone(flow));
        setStatusMsg(`Saved. step_count_before → step_count_after available in audit_log.`);
      }
    } catch (e) {
      setError(`save_failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
    // Silence the unused dispatch import — kept for future direct-dispatch
    // paths; current Save uses raw fetch so the route runs without
    // relying on the generic dispatch helper's stable URL shape.
    void dispatchAction;
  }, [flow, flow_name]);

  const handleReset = useCallback(() => {
    if (original === null) return;
    setFlow(structuredClone(original));
    setStatusMsg("Reverted to last-saved state");
  }, [original]);

  const handleReload = useCallback(async () => {
    if (flow_name === undefined) return;
    setReloading(true);
    setStatusMsg(null);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token ?? "";
      const res = await fetch(`/api/flows/${flow_name}/reload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        setStatusMsg(
          "Reload signal sent. The receiver picks up the new YAML on the next saga invocation.",
        );
      } else {
        const body = await res.text();
        setError(`reload_failed (HTTP ${res.status}): ${body}`);
      }
    } catch (e) {
      setError(`reload_failed: ${(e as Error).message}`);
    } finally {
      setReloading(false);
    }
  }, [flow_name]);

  if (loading) {
    return (
      <div className="page-flow-edit">
        <h1>Edit flow: {flow_name}</h1>
        <p>Loading…</p>
      </div>
    );
  }
  if (error !== null && flow === null) {
    return (
      <div className="page-flow-edit">
        <h1>Edit flow: {flow_name}</h1>
        <div className="tag tag-danger">Error: {error}</div>
        <p>
          <button onClick={() => navigate("/flows")}>Back to /flows</button>
        </p>
      </div>
    );
  }
  if (flow === null) return <div>No flow loaded.</div>;

  return (
    <div className="page-flow-edit">
      <div>
        <h1>Edit flow: {flow.name}</h1>
        <p className="subtle">
          Studio: <code>{studioSlug}</code> · YAML at{" "}
          <code>
            verticals/ems/studios/{studioSlug}/flows/{flow.name}.yaml
          </code>
        </p>
        {error !== null && <div className="tag tag-danger">Error: {error}</div>}
        {statusMsg !== null && (
          <div className="tag tag-success">{statusMsg}</div>
        )}

        <fieldset>
          <legend>Flow header</legend>
          <div className="form-row">
            <label>Name (read-only — file name)</label>
            <input type="text" value={flow.name} readOnly />
          </div>
          <div className="form-row">
            <label>Trigger event</label>
            <select
              value={flow.trigger_event}
              onChange={(e) =>
                setFlow({ ...flow, trigger_event: e.target.value })
              }
            >
              {MARKETING_FLOW_EVENT_NAMES.map((ev) => (
                <option key={ev} value={ev}>
                  {ev}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>Exit events</label>
            <div className="exit-events-checklist">
              {MARKETING_FLOW_EVENT_NAMES.map((ev) => {
                const checked = flow.exit_events.includes(ev);
                return (
                  <label key={ev}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...flow.exit_events, ev]
                          : flow.exit_events.filter((x) => x !== ev);
                        setFlow({ ...flow, exit_events: next });
                      }}
                    />
                    {ev}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="form-row">
            <label>Exit after hours</label>
            <input
              type="number"
              min={1}
              max={720}
              value={flow.exit_after_hours}
              onChange={(e) =>
                setFlow({
                  ...flow,
                  exit_after_hours: Number.parseInt(e.target.value, 10),
                })
              }
            />
          </div>
          {/*
            Mario's 2026-09-15 brief: "an option for the user (i.e. admin in
            this case) for how many times the last step of the flow should
            repeat. Infinite should be an option too." The unit is a SEGMENT,
            not a step — `last_steps: 2` loops the final email + WhatsApp pair
            together — which is why there are two controls rather than one.
          */}
          <div className="form-row">
            <label>Repeat the last</label>
            <input
              type="number"
              min={1}
              max={flow.steps.length}
              disabled={flow.repeat === undefined}
              value={flow.repeat?.last_steps ?? 1}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                if (!Number.isFinite(n) || flow.repeat === undefined) return;
                setFlow({ ...flow, repeat: { ...flow.repeat, last_steps: n } });
              }}
            />
            <span> step(s), </span>
            <select
              value={
                flow.repeat === undefined
                  ? "off"
                  : flow.repeat.times === "infinite"
                    ? "infinite"
                    : "count"
              }
              onChange={(e) => {
                const mode = e.target.value;
                if (mode === "off") {
                  // Explicitly drop the key — a flow with no repeat has no
                  // `repeat` block at all, which is what the loader expects.
                  const { repeat: _dropped, ...rest } = flow;
                  void _dropped;
                  setFlow(rest as FlowShape);
                  return;
                }
                const lastSteps = flow.repeat?.last_steps ?? 1;
                setFlow({
                  ...flow,
                  repeat: {
                    last_steps: lastSteps,
                    times: mode === "infinite" ? "infinite" : 3,
                  },
                });
              }}
            >
              <option value="off">no repeat</option>
              <option value="count">a set number of times</option>
              <option value="infinite">forever (infinite)</option>
            </select>
            {flow.repeat !== undefined && flow.repeat.times !== "infinite" ? (
              <input
                type="number"
                min={1}
                max={500}
                value={flow.repeat.times}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  if (!Number.isFinite(n) || flow.repeat === undefined) return;
                  setFlow({ ...flow, repeat: { ...flow.repeat, times: n } });
                }}
              />
            ) : null}
          </div>
          {flow.repeat?.times === "infinite" ? (
            <p className="form-hint">
              A run is capped at 365 days, so an infinite loop is carried into
              the next run by the daily renewal — the member keeps looping
              until they convert or opt out.
            </p>
          ) : null}
        </fieldset>

        <fieldset>
          <legend>Steps ({flow.steps.length})</legend>
          {flow.steps.map((step, idx) => (
            <StepEditor
              key={idx}
              index={idx}
              step={step}
              flowName={flow.name}
              sendActions={sendActionOptions}
              triggerEvent={flow.trigger_event}
              onChange={(updated) => {
                const next = flow.steps.slice();
                next[idx] = updated;
                setFlow({ ...flow, steps: next });
              }}
              onRemove={() => {
                const next = flow.steps.slice();
                next.splice(idx, 1);
                setFlow({ ...flow, steps: next });
              }}
              onMoveUp={() => {
                if (idx === 0) return;
                const next = flow.steps.slice();
                const [a, b] = [next[idx - 1]!, next[idx]!];
                next[idx - 1] = b;
                next[idx] = a;
                setFlow({ ...flow, steps: next });
              }}
              onMoveDown={() => {
                if (idx >= flow.steps.length - 1) return;
                const next = flow.steps.slice();
                const [a, b] = [next[idx]!, next[idx + 1]!];
                next[idx] = b;
                next[idx + 1] = a;
                setFlow({ ...flow, steps: next });
              }}
            />
          ))}
          <p>
            <button
              onClick={() => {
                const next = flow.steps.slice();
                next.push({
                  delay_after_previous_hours: 24,
                  action: sendActionOptions[0] ?? "send_email",
                  input_template: { subject: "", text_body: "" },
                });
                setFlow({ ...flow, steps: next });
              }}
            >
              + Add step
            </button>
          </p>
        </fieldset>

        {WA_COMPANION_TEMPLATE[flow.name] !== undefined && (
          <fieldset>
            <legend>WhatsApp companion (view-only)</legend>
            <div style={{ fontSize: 13, color: "#5c6069", lineHeight: 1.6 }}>
              When this flow is on, the same event also sends a Meta-approved
              WhatsApp template to members who acted outside their own chat (via
              the <code>member_transactional_wa</code> saga, riding this same
              toggle). Its wording is managed in WhatsApp Manager — editing it
              there requires re-approval by Meta, so it is not editable here.
              <div style={{ marginTop: 10 }}>
                <strong>Template:</strong>{" "}
                <code>{WA_COMPANION_TEMPLATE[flow.name]!.name}</code>
                {WA_COMPANION_TEMPLATE[flow.name]!.homeName !== undefined && (
                  <>
                    {" "}
                    (home-session variant{" "}
                    <code>{WA_COMPANION_TEMPLATE[flow.name]!.homeName}</code>)
                  </>
                )}
                <br />
                <strong>Variables:</strong>{" "}
                {WA_COMPANION_TEMPLATE[flow.name]!.vars}
              </div>
            </div>
          </fieldset>
        )}

        <div className="form-row" style={{ display: "flex", gap: 8 }}>
          <button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={handleReset} disabled={saving}>
            Reset to disk
          </button>
          <button onClick={handleReload} disabled={reloading}>
            {reloading ? "Sending signal…" : "Trigger reload (dev)"}
          </button>
          <button onClick={() => navigate("/flows")} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface StepEditorProps {
  index: number;
  step: FlowStepShape;
  flowName: string;
  sendActions: ReadonlyArray<string>;
  triggerEvent: string;
  onChange: (next: FlowStepShape) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function migrateInputTemplate(
  fromAction: string,
  toAction: string,
  current: Record<string, unknown>,
): Record<string, unknown> {
  if (fromAction === toAction) return current;
  if (toAction === "send_whatsapp_message") {
    const carriedBody =
      typeof current["body"] === "string" && current["body"]
        ? current["body"]
        : typeof current["text_body"] === "string"
          ? current["text_body"]
          : "";
    return { body: carriedBody };
  }
  if (toAction === "send_email") {
    const carriedText =
      typeof current["text_body"] === "string" && current["text_body"]
        ? current["text_body"]
        : typeof current["body"] === "string"
          ? current["body"]
          : "";
    const out: Record<string, unknown> = {
      subject: typeof current["subject"] === "string" ? current["subject"] : "",
      text_body: carriedText,
    };
    if (typeof current["html_body"] === "string" && current["html_body"]) {
      out["html_body"] = current["html_body"];
    }
    return out;
  }
  return current;
}

function StepEditor({
  index,
  step,
  flowName,
  sendActions,
  triggerEvent,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: StepEditorProps): JSX.Element {
  const navigate = useNavigate();
  const input = step.input_template;
  const isEmail = step.action === "send_email";
  const isWhatsApp = step.action === "send_whatsapp_message";
  const emailKind = emailKindForStep(flowName, step, index);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const onView = useCallback(async () => {
    if (emailKind === null) return;
    setViewLoading(true);
    try {
      const r = await api.get<{ kind: string; html: string }>(
        `/api/email-templates/${encodeURIComponent(emailKind)}/preview`,
      );
      setPreviewHtml(r.html);
    } catch (e) {
      setPreviewHtml(
        `<p style="font-family:sans-serif;padding:20px;">Preview failed: ${
          e instanceof ApiError ? `HTTP ${e.status}` : (e as Error).message
        }</p>`,
      );
    } finally {
      setViewLoading(false);
    }
  }, [emailKind]);
  // Phase 34 sub-decision F — wake target widget. Two modes:
  //   - "relative" → uses delay_after_previous_hours, no anchor.
  //   - "anchor"   → uses {trigger_field, offset_hours}; delay is reset to 0.
  const wakeMode: "relative" | "anchor" =
    step.anchor !== undefined ? "anchor" : "relative";
  const anchorFields: ReadonlyArray<string> =
    MARKETING_FLOW_EVENT_FIELDS[triggerEvent] ?? [];
  return (
    <div
      style={{
        border: "1px solid #444",
        borderRadius: 4,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <strong>Step {index + 1}</strong>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={onMoveUp}>↑</button>
          <button onClick={onMoveDown}>↓</button>
          <button onClick={onRemove}>Remove</button>
        </div>
      </div>
      <div className="form-row">
        <label>Wake target</label>
        <select
          value={wakeMode}
          onChange={(e) => {
            const next = e.target.value as "relative" | "anchor";
            if (next === "anchor") {
              // Switch to anchor mode — preserve delay as a hint of
              // intent (becomes the offset's initial value when
              // there's a sensible default). Otherwise default to
              // first allowed field + 0 offset.
              const defaultField = anchorFields[0] ?? "starts_at";
              onChange({
                ...step,
                delay_after_previous_hours: 0,
                anchor: { trigger_field: defaultField, offset_hours: 0 },
              });
            } else {
              // Switch to relative mode — drop the anchor; preserve
              // delay (0 by default).
              const { anchor, ...rest } = step;
              void anchor;
              onChange({ ...rest });
            }
          }}
        >
          <option value="relative">Relative (delay after previous step)</option>
          <option value="anchor">Anchor (relative to trigger event field)</option>
        </select>
      </div>
      {wakeMode === "relative" && (
        <div className="form-row">
          <label>Delay (hours)</label>
          <input
            type="number"
            min={0}
            max={720}
            value={step.delay_after_previous_hours}
            onChange={(e) =>
              onChange({
                ...step,
                delay_after_previous_hours:
                  Number.parseInt(e.target.value, 10) || 0,
              })
            }
          />
        </div>
      )}
      {wakeMode === "anchor" && step.anchor !== undefined && (
        <>
          <div className="form-row">
            <label>Anchor trigger field</label>
            <select
              value={step.anchor.trigger_field}
              onChange={(e) =>
                onChange({
                  ...step,
                  anchor: {
                    trigger_field: e.target.value,
                    offset_hours: step.anchor!.offset_hours,
                  },
                })
              }
            >
              {anchorFields.length === 0 ? (
                <option value="">
                  (No date fields available for trigger '{triggerEvent}')
                </option>
              ) : (
                anchorFields.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))
              )}
            </select>
            <small className="subtle">
              Wake-time pinned to <code>{triggerEvent}</code>.
              <code>{step.anchor.trigger_field}</code>
            </small>
          </div>
          <div className="form-row">
            <label>Offset (hours; signed: negative = before, positive = after)</label>
            <input
              type="number"
              min={-720}
              max={720}
              value={step.anchor.offset_hours}
              onChange={(e) =>
                onChange({
                  ...step,
                  anchor: {
                    trigger_field: step.anchor!.trigger_field,
                    offset_hours: Number.parseInt(e.target.value, 10) || 0,
                  },
                })
              }
            />
          </div>
        </>
      )}
      <div className="form-row">
        <label>Action</label>
        <select
          value={step.action}
          onChange={(e) =>
            onChange({
              ...step,
              action: e.target.value,
              input_template: migrateInputTemplate(
                step.action,
                e.target.value,
                step.input_template,
              ),
            })
          }
        >
          {sendActions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>
      <div className="form-row">
        <label>Opt-out check (key in members.metadata.flow_opt_outs)</label>
        <input
          type="text"
          value={step.opt_out_check ?? ""}
          placeholder="e.g. welcome_emails"
          onChange={(e) => {
            const v = e.target.value;
            if (v.length === 0) {
              const { opt_out_check, ...rest } = step;
              void opt_out_check;
              onChange({ ...rest });
            } else {
              onChange({ ...step, opt_out_check: v });
            }
          }}
        />
      </div>
      {isEmail && (
        <>
          <div className="form-row">
            <label>Subject</label>
            <input
              type="text"
              value={String(input["subject"] ?? "")}
              onChange={(e) =>
                onChange({
                  ...step,
                  input_template: { ...input, subject: e.target.value },
                })
              }
            />
          </div>
          <div className="form-row">
            <label>Text body</label>
            <textarea
              value={String(input["text_body"] ?? "")}
              rows={8}
              onChange={(e) =>
                onChange({
                  ...step,
                  input_template: { ...input, text_body: e.target.value },
                })
              }
            />
          </div>
          <div className="form-row">
            <label>Email design</label>
            {emailKind !== null ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <code>{emailKind}</code>
                <button type="button" onClick={onView} disabled={viewLoading}>
                  {viewLoading ? "Loading…" : "View"}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/email-templates/${encodeURIComponent(emailKind)}/edit`,
                    )
                  }
                >
                  Edit
                </button>
                <small className="subtle">
                  The branded HTML email — <strong>View</strong> the render, or{" "}
                  <strong>Edit</strong> its full HTML (live on the next send;
                  the subject + plaintext above stay as the fallback twin).
                </small>
              </div>
            ) : (
              <small className="subtle">
                No HTML design set for this step — it sends plaintext only.
              </small>
            )}
          </div>
        </>
      )}
      {isWhatsApp && (
        <>
          {step.template !== undefined && step.template.length > 0 && (
            <div className="form-row">
              <label>WhatsApp template (view-only)</label>
              <small className="subtle">
                <code>{step.template}</code> — a Meta-approved template. Its
                wording is managed in WhatsApp Manager (edit + re-submit for
                approval there), not here; the body below is the fallback /
                variable content.
              </small>
            </div>
          )}
          <div className="form-row">
            <label>Body (WhatsApp text)</label>
            <textarea
              value={String(input["body"] ?? "")}
              rows={6}
              onChange={(e) =>
                onChange({
                  ...step,
                  input_template: { ...input, body: e.target.value },
                })
              }
            />
          </div>
        </>
      )}
      {!isEmail && !isWhatsApp && (
        <div className="form-row">
          <label>Raw input template (JSON)</label>
          <textarea
            value={JSON.stringify(input, null, 2)}
            rows={6}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
                  onChange({ ...step, input_template: parsed });
                }
              } catch {
                // Don't propagate parse errors live; user has to fix JSON.
              }
            }}
          />
        </div>
      )}
      {previewHtml !== null && (
        <EmailPreviewModal
          title={`Preview · ${emailKind ?? "email"}`}
          subtitle="The rendered email with sample values filled in."
          html={previewHtml}
          onClose={() => setPreviewHtml(null)}
        />
      )}
    </div>
  );
}
